import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaTimes, FaCar, FaUser, FaUserTie, FaCogs, FaTools, FaTag, FaUniversity, FaFileContract, FaHandshake, FaCheckCircle, FaMapMarkerAlt, FaCalendarCheck, FaOilCan, FaMoneyBill, FaFileInvoice, FaFileInvoiceDollar, FaBuilding, FaMobile, FaChartLine, FaBell } from 'react-icons/fa';
import { MdDashboard, MdInventory, MdHistory, MdHelpCenter, MdWhatsapp, MdDiamond } from 'react-icons/md';
import { useTabNavigation } from '../../hooks/useTabNavigation';
import { IconType } from 'react-icons';

const ICON_MAP: Record<string, IconType> = {
  dashboard: MdDashboard,
  car: FaCar,
  user: FaUser,
  'user-tie': FaUserTie,
  cogs: FaCogs,
  tools: FaTools,
  tag: FaTag,
  university: FaUniversity,
  'file-contract': FaFileContract,
  handshake: FaHandshake,
  'check-circle': FaCheckCircle,
  map: FaMapMarkerAlt,
  'calendar-check': FaCalendarCheck,
  'oil-can': FaOilCan,
  inventory: MdInventory,
  'invoice-dollar': FaFileInvoiceDollar,
  money: FaMoneyBill,
  invoice: FaFileInvoice,
  chart: FaChartLine,
  history: MdHistory,
  help: MdHelpCenter,
  whatsapp: MdWhatsapp,
  diamond: MdDiamond,
  bell: FaBell,
  building: FaBuilding,
  mobile: FaMobile,
};

function getIcon(iconKey?: string): IconType {
  if (!iconKey) return MdDashboard;
  return ICON_MAP[iconKey] || MdDashboard;
}

const TabBarContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f5f6fa;
  border-bottom: 1px solid #e0e0e0;
  min-height: 38px;
  max-height: 38px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-height: 34px;
    max-height: 34px;
  }
`;

const TabsScrollContainer = styled.div`
  display: flex;
  align-items: center;
  overflow-x: auto;
  flex: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  height: 38px;
  min-width: fit-content;
  white-space: nowrap;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? '#1E47A8' : '#555')};
  background: ${({ active }) => (active ? '#fff' : 'transparent')};
  border-bottom: ${({ active }) => (active ? '2.5px solid #1E47A8' : '2.5px solid transparent')};
  border-right: 1px solid #e8e8e8;
  transition: all 0.15s ease;
  user-select: none;
  position: relative;

  &:hover {
    background: ${({ active }) => (active ? '#fff' : '#eef1f7')};
    color: ${({ active }) => (active ? '#1E47A8' : '#333')};
  }

  svg.tab-icon {
    font-size: 13px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 0 10px;
    height: 34px;
    font-size: 11.5px;
  }
`;

const CloseButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-left: 4px;
  opacity: 0.4;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
    background: #ddd;
    color: #c0392b;
  }

  svg {
    font-size: 9px;
  }
`;

const ContextMenu = styled.div<{ x: number; y: number }>`
  position: fixed;
  top: ${({ y }) => y}px;
  left: ${({ x }) => x}px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  z-index: 10000;
  min-width: 180px;
  padding: 4px 0;
`;

const ContextMenuItem = styled.div`
  padding: 8px 16px;
  font-size: 13px;
  color: #333;
  cursor: pointer;

  &:hover {
    background: #f0f4ff;
    color: #1E47A8;
  }
`;

interface TabContextMenuProps {
  tabs: Array<{ id: string; closable: boolean }>;
  contextMenu: { tabId: string; x: number; y: number };
  closeTab: (id: string) => void;
  closeOtherTabs: (id: string) => void;
  closeTabsRight: (id: string) => void;
  closeAllTabs: () => void;
}

const TabContextMenu: React.FC<TabContextMenuProps> = ({ tabs, contextMenu, closeTab, closeOtherTabs, closeTabsRight, closeAllTabs }) => {
  const ctxTab = tabs.find(t => t.id === contextMenu.tabId);
  const ctxIdx = tabs.findIndex(t => t.id === contextMenu.tabId);
  const hasClosableTabs = tabs.some(t => t.closable);
  const hasOtherClosable = tabs.some(t => t.closable && t.id !== contextMenu.tabId);
  const hasClosableRight = tabs.some((t, i) => i > ctxIdx && t.closable);

  return (
    <ContextMenu x={contextMenu.x} y={contextMenu.y}>
      {ctxTab?.closable && (
        <ContextMenuItem onClick={() => closeTab(contextMenu.tabId)}>Fechar</ContextMenuItem>
      )}
      {hasOtherClosable && (
        <ContextMenuItem onClick={() => closeOtherTabs(contextMenu.tabId)}>Fechar outras</ContextMenuItem>
      )}
      {hasClosableRight && (
        <ContextMenuItem onClick={() => closeTabsRight(contextMenu.tabId)}>Fechar à direita</ContextMenuItem>
      )}
      {hasClosableTabs && (
        <ContextMenuItem onClick={() => closeAllTabs()}>Fechar todas</ContextMenuItem>
      )}
    </ContextMenu>
  );
};

const TabBar: React.FC = () => {
  const { tabs, activeTabId, navigateToTab, closeTab, closeOtherTabs, closeTabsRight, closeAllTabs } = useTabNavigation();
  const [contextMenu, setContextMenu] = useState<{ tabId: string; x: number; y: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);

  // Scroll para tab ativa quando muda
  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [activeTabId]);

  // Fechar context menu ao clicar fora
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenu]);

  const handleContextMenu = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    setContextMenu({ tabId, x: e.clientX, y: e.clientY });
  }, []);

  const handleTabClick = useCallback((tabId: string) => {
    navigateToTab(tabId);
  }, [navigateToTab]);

  const handleCloseClick = useCallback((e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    closeTab(tabId);
  }, [closeTab]);

  // Scroll horizontal com mouse wheel
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft += e.deltaY;
    }
  }, []);

  if (tabs.length <= 1) return null;

  return (
    <>
      <TabBarContainer onWheel={handleWheel}>
        <TabsScrollContainer ref={scrollRef}>
          {tabs.map((tab) => {
            const Icon = getIcon(tab.icon);
            const isActive = tab.id === activeTabId;
            return (
              <TabItem
                key={tab.id}
                active={isActive}
                ref={isActive ? activeTabRef : undefined}
                onClick={() => handleTabClick(tab.id)}
                onContextMenu={(e) => handleContextMenu(e, tab.id)}
                title={tab.title}
              >
                <Icon className="tab-icon" />
                <span>{tab.title}</span>
                {tab.closable && (
                  <CloseButton onClick={(e) => handleCloseClick(e, tab.id)}>
                    <FaTimes />
                  </CloseButton>
                )}
              </TabItem>
            );
          })}
        </TabsScrollContainer>
      </TabBarContainer>

      {contextMenu && <TabContextMenu
        tabs={tabs}
        contextMenu={contextMenu}
        closeTab={(id) => { closeTab(id); setContextMenu(null); }}
        closeOtherTabs={(id) => { closeOtherTabs(id); setContextMenu(null); }}
        closeTabsRight={(id) => { closeTabsRight(id); setContextMenu(null); }}
        closeAllTabs={() => { closeAllTabs(); setContextMenu(null); }}
      />}
    </>
  );
};

export default TabBar;
